import {
  Contract,
  EnergyContractDetails,
  GetCustomerContractsResponse,
  LatestTariff,
  TimeOfUseLabel,
  ClockingTimeLabel,
  ContractFuel,
  ContractType,
  EnergyContractResponse,
  EnergyContracts,
  UnitRates,
} from '../types';

export const mapResponseToClientShape = (
  response: GetCustomerContractsResponse,
): EnergyContracts => {
  return mapContractsToClientShapeByFuel(response);
};

function mapContractsToClientShapeByFuel(
  response: GetCustomerContractsResponse,
): EnergyContracts {
  return response.data.reduce<EnergyContracts>(
    (contracts, currentContract) => {
      const mappedContract = getMappedClientShape(currentContract);
      if (!mappedContract) {
        return contracts;
      }
      if (mappedContract.fuel === ContractFuel.ELEC) {
        return {
          electricity: [...contracts.electricity, mappedContract],
          gas: contracts.gas,
        };
      }

      if (mappedContract.fuel === ContractFuel.GAS) {
        return {
          electricity: contracts.electricity,
          gas: [...contracts.gas, mappedContract],
        };
      }

      return contracts;
    },
    {
      electricity: [],
      gas: [],
    },
  );
}

const toEpochDays = (dateString: string): number => {
  const date = new Date(dateString);
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.floor(date.getTime() / MS_PER_DAY);
};

const getContractTypeFromLatestTariff = (
  type: LatestTariff['type'],
): ContractType => {
  if (type === 'FIXED') {
    return ContractType.FIXED;
  }
  return ContractType.VARIABLE;
};

const getFuelFromEnergyContractDetails = (
  fuel: EnergyContractDetails['fuel'],
): ContractFuel => {
  if (fuel === 'ELECTRICITY') {
    return ContractFuel.ELEC;
  }
  return ContractFuel.GAS;
};

const mapTimingCategoryToClockingTimeLabel = (
  timingCategory: string,
):
  | {
      clockingTimeLabel: ClockingTimeLabel;
      timeOfUseLabel: TimeOfUseLabel | undefined;
    }
  | undefined => {
  switch (timingCategory) {
    case 'EVENING_AND_WEEKEND':
      return {
        clockingTimeLabel: ClockingTimeLabel.EVENING_AND_WEEKEND,
        timeOfUseLabel: TimeOfUseLabel.eveningAndWeekend,
      };
    case 'HEATING':
      return {
        clockingTimeLabel: ClockingTimeLabel.HEATING,
        timeOfUseLabel: TimeOfUseLabel.heating,
      };
    case 'OFF_PEAK':
      return {
        clockingTimeLabel: ClockingTimeLabel.NIGHT,
        timeOfUseLabel: TimeOfUseLabel.offpeak,
      };
    case 'PEAK':
      return {
        clockingTimeLabel: ClockingTimeLabel.DAY,
        timeOfUseLabel: TimeOfUseLabel.peak,
      };
    case 'ANYTIME':
      return {
        clockingTimeLabel: ClockingTimeLabel.ANYTIME,
        timeOfUseLabel: TimeOfUseLabel.anytime,
      };
    default:
      return undefined;
  }
};

const getUnitRatesFromConsumptionChargeRates = (
  consumptionChargeRates: LatestTariff['consumptionChargeRates'],
): UnitRates => {
  return consumptionChargeRates.reduce<UnitRates>((acc, rate) => {
    const rates = mapTimingCategoryToClockingTimeLabel(rate.timingCategory);
    if (!rates) {
      return acc;
    }

    return [
      ...acc,
      {
        unitRate: Number(rate.defaultRate.value),
        clockingTimeLabel: rates.clockingTimeLabel,
        timeOfUseLabel: rates.timeOfUseLabel,
      },
    ];
  }, []);
};

const getPaymentMethodFromResponse = (
  paymentMethod: LatestTariff['paymentMethod'],
): EnergyContractResponse['paymentMethod'] => {
  switch (paymentMethod) {
    case 'DIRECT_DEBIT':
      return 'Direct Debit';
    case 'PREPAYMENT':
      return 'Prepayment';
    default:
      return paymentMethod;
  }
};

function getMappedClientShape(
  contract: Contract,
): EnergyContractResponse | undefined {
  return {
    contractId: contract.contractId,
    meterType: contract.details.meter.type || null,
    mpxn: contract.details.mpxn,
    msn: contract.details.meter.msn || null,
    relatedSupplies: contract.details.relatedSupplies,
    lossDate: contract.details.lossDate || null,
    profileClass: contract.details.meter.profileClass || '',
    startDate: toEpochDays(contract.startDate),
    paymentMethod: getPaymentMethodFromResponse(
      contract.details.latestTariff.paymentMethod,
    ),
    expiryDate: contract.endDate ? toEpochDays(contract.endDate) : null,
    type: getContractTypeFromLatestTariff(contract.details.latestTariff.type),
    fuel: getFuelFromEnergyContractDetails(contract.details.fuel),
    standingRate: Number(contract.details.latestTariff.standingRate.value),
    exitFee: Number(contract.details.latestTariff.exitFee.value) * 100, // Convert to pence to match legacy OREX response
    planName: contract.details.latestTariff.name,
    serviceAccountId: contract.accountId,
    lengthInMonths: contract.details.latestTariff.lengthInMonths || null,
    region: contract.details.latestTariff.regionMapping.value,
    unitRates: getUnitRatesFromConsumptionChargeRates(
      contract.details.latestTariff.consumptionChargeRates,
    ),
    status: {
      active: contract.details.status.active,
      inRenewal: contract.details.status.inRenewal || false,
      inLoss: contract.details.status.inLoss || false,
      lossComplete: contract.details.status.lossComplete || false,
      hasFutureContracts: contract.details.status.hasFutureContracts,
    },
  };
}
